const BOOTSTRAP_URL = '/api/bootstrap.php';
const STORAGE_URL = '/api/storage.php';

let initialized = false;
let syncingDisabled = false;
let originalSetItem = null;
let originalRemoveItem = null;
let originalClear = null;
const pendingWrites = new Map();
let flushTimer = null;

const isBrowser = () => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const scheduleFlush = () => {
  if (flushTimer) return;
  flushTimer = window.setTimeout(async () => {
    flushTimer = null;
    const entries = [...pendingWrites.entries()];
    pendingWrites.clear();
    await Promise.all(entries.map(async ([key, payload]) => {
      try {
        await fetch(STORAGE_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          credentials: 'same-origin'
        });
      } catch (error) {
        pendingWrites.set(key, payload);
      }
    }));
  }, 150);
};

const queueWrite = (key, payload) => {
  pendingWrites.set(key, payload);
  scheduleFlush();
};

const seedLocalStorage = (items = []) => {
  syncingDisabled = true;
  try {
    items.forEach(({ key, value }) => {
      if (!key) return;
      window.localStorage.setItem(key, value ?? '');
    });
  } finally {
    syncingDisabled = false;
  }
};

const syncExistingSnapshot = () => {
  for (let index = 0; index < window.localStorage.length; index += 1) {
    const key = window.localStorage.key(index);
    if (!key) continue;
    queueWrite(key, {
      operation: 'upsert',
      key,
      value: window.localStorage.getItem(key) ?? ''
    });
  }
};

const patchStorage = () => {
  if (originalSetItem) return;
  originalSetItem = Storage.prototype.setItem;
  originalRemoveItem = Storage.prototype.removeItem;
  originalClear = Storage.prototype.clear;

  Storage.prototype.setItem = function patchedSetItem(key, value) {
    originalSetItem.call(this, key, value);
    if (!syncingDisabled && this === window.localStorage) {
      queueWrite(key, {
        operation: 'upsert',
        key,
        value
      });
    }
  };

  Storage.prototype.removeItem = function patchedRemoveItem(key) {
    originalRemoveItem.call(this, key);
    if (!syncingDisabled && this === window.localStorage) {
      queueWrite(key, {
        operation: 'delete',
        key
      });
    }
  };

  Storage.prototype.clear = function patchedClear() {
    originalClear.call(this);
    if (!syncingDisabled && this === window.localStorage) {
      queueWrite('__clear__', { operation: 'clear' });
    }
  };
};

export async function initializeServerPersistence() {
  if (!isBrowser() || initialized) return;
  initialized = true;
  patchStorage();

  try {
    const response = await fetch(BOOTSTRAP_URL, {
      credentials: 'same-origin',
      cache: 'no-store'
    });
    const payload = await response.json();
    if (response.ok && payload?.ok && Array.isArray(payload.items)) {
      seedLocalStorage(payload.items);
    }
  } catch (error) {
    // Keep the app usable offline or before the API is deployed.
  }

  syncExistingSnapshot();
}
